import React from 'react'
import { Link, MenuItem, TextField, Typography } from '@material-ui/core'
import { createStyles, makeStyles, Theme } from '@material-ui/core/styles'

import palette from '../../../../themes/palette'
import type { ConversationOption, Mailer, TopicOption } from '../../../../api/EmailTestBenchApi'
import {
  CONTEXT_CHANGE,
  CONTEXT_CONVERSATION_EMPTY,
  CONTEXT_HEADINGS,
  CONTEXT_LOADING,
  CONTEXT_MEMBERSHIP,
  CONTEXT_NONE,
  CONTEXT_TOPIC_EMPTY,
  CONTEXT_USING
} from './copy'

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    panel: {
      border: `1px solid ${palette.lightestgrey}`,
      borderRadius: 8,
      padding: theme.spacing(1.5, 2),
      marginBottom: theme.spacing(2),
      backgroundColor: palette.nothinGrey
    },
    // "Nothing to choose" is drawn as an absence rather than as another form
    // control -- the point of the conditional picker is that seven of these
    // emails have nothing to pick.
    panelNone: { backgroundColor: 'transparent', borderStyle: 'dashed' },
    heading: {
      textTransform: 'uppercase',
      letterSpacing: '0.08em',
      fontSize: 11,
      color: palette.lightergrey,
      marginBottom: theme.spacing(0.5)
    },
    picker: { marginTop: theme.spacing(1), maxWidth: 460 }
  })
)

interface Props {
  mailer: Mailer
  conversations: ConversationOption[]
  topics: TopicOption[]
  loading: boolean
  conversationId?: number
  topicId?: number
  onConversationChange(id: number): void
  onTopicChange(id: number): void
}

const describeConversation = (conversation?: ConversationOption): string => {
  if (!conversation) return ''
  const date = conversation.starts_at ? new Date(conversation.starts_at).toLocaleDateString() : null
  return [conversation.title, date, `${conversation.guest_count} guests`, `#${conversation.id}`]
    .filter(Boolean)
    .join(' · ')
}

/**
 * The conditional context panel.
 *
 * 🔴 THE DESIGN RULE THIS ENFORCES: the picker is driven by the SELECTED
 * MAILER's argument shape, not by a global setting. Select "Password reset" and
 * there is nothing to choose, so nothing is offered. Select "You're going —
 * confirmation" and a conversation picker appears. `context_kind` comes off the
 * server, which reflects it off EmailService's own parameters -- so this stays
 * right when a mailer is added, without anyone editing a list here.
 *
 * 36 of the 48 need a conversation, 2 need a topic, 1 needs a membership (with
 * no picker -- the bench uses the first record), 7 need nothing at all, and 3
 * cannot be run.
 */
const ContextPanel: React.FC<Props> = ({
  mailer,
  conversations,
  topics,
  loading,
  conversationId,
  topicId,
  onConversationChange,
  onTopicChange
}) => {
  const classes = useStyles()
  const [changing, setChanging] = React.useState(false)

  const kind = mailer.context_kind
  const heading = CONTEXT_HEADINGS[kind] || CONTEXT_HEADINGS.none

  const body = () => {
    if (kind === 'none') return <Typography variant='body2'>{CONTEXT_NONE}</Typography>
    if (kind === 'membership') return <Typography variant='body2'>{CONTEXT_MEMBERSHIP}</Typography>
    if (kind === 'locked') return <Typography variant='body2'>{mailer.reason}</Typography>

    if (loading) return <Typography variant='body2'>{CONTEXT_LOADING}</Typography>

    if (kind === 'topic') {
      if (topics.length === 0) return <Typography variant='body2'>{CONTEXT_TOPIC_EMPTY}</Typography>
      const selected = topics.find((t) => t.id === topicId) || topics[0]

      return (
        <>
          <Typography variant='body2'>
            {CONTEXT_USING} <strong>{selected.title}</strong> · {selected.slug}
            {!changing && (
              <>
                {' — '}
                <Link component='button' variant='body2' onClick={() => setChanging(true)}>
                  {CONTEXT_CHANGE}
                </Link>
              </>
            )}
          </Typography>
          {changing && (
            <TextField
              select
              fullWidth
              size='small'
              variant='outlined'
              className={classes.picker}
              id='ic-bench-topic-picker'
              value={selected.id}
              onChange={(e) => {
                onTopicChange(Number(e.target.value))
                setChanging(false)
              }}
            >
              {topics.map((topic) => (
                <MenuItem key={topic.id} value={topic.id}>
                  {topic.title} · {topic.conversation_count} conversations
                </MenuItem>
              ))}
            </TextField>
          )}
        </>
      )
    }

    if (conversations.length === 0) {
      return <Typography variant='body2'>{CONTEXT_CONVERSATION_EMPTY}</Typography>
    }

    const selected =
      conversations.find((c) => c.id === conversationId) ||
      conversations.find((c) => c.default) ||
      conversations[0]

    return (
      <>
        {/* A sentence, not a form. Nobody should have to know a conversation id
            to look at an email; "Change" is the escape hatch, and it opens a
            short list rather than a search box. */}
        <Typography variant='body2'>
          {CONTEXT_USING} <strong>{describeConversation(selected)}</strong>
          {!changing && (
            <>
              {' — '}
              <Link component='button' variant='body2' onClick={() => setChanging(true)}>
                {CONTEXT_CHANGE}
              </Link>
            </>
          )}
        </Typography>
        {changing && (
          <TextField
            select
            fullWidth
            size='small'
            variant='outlined'
            className={classes.picker}
            id='ic-bench-conversation-picker'
            value={selected.id}
            onChange={(e) => {
              onConversationChange(Number(e.target.value))
              setChanging(false)
            }}
          >
            {conversations.map((conversation) => (
              <MenuItem key={conversation.id} value={conversation.id}>
                {describeConversation(conversation)}
              </MenuItem>
            ))}
          </TextField>
        )}
      </>
    )
  }

  return (
    <div
      className={`${classes.panel} ${kind === 'none' ? classes.panelNone : ''}`}
      id='ic-bench-context'
      data-context-kind={kind}
    >
      <div className={classes.heading}>{heading}</div>
      {body()}
    </div>
  )
}

export default ContextPanel
